import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TrendingUp,
  DollarSign,
  PieChart,
  FileText,
  Users,
  Package,
  Calendar,
  ArrowUpRight,
  ArrowDownRight,
  BarChart3,
  Wallet,
  Receipt,
  Target,
  Loader2,
  Book,
  ClipboardList,
  Download,
  X
} from 'lucide-react';
import { accountingAPI, salesAPI, watchAPI } from '../services/api';

const AccountingDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('month'); // 'week', 'month', 'quarter', 'year'
  const [error, setError] = useState(null);

  // Real data from API
  const [metrics, setMetrics] = useState({
    totalRevenue: 0,
    totalExpenses: 0,
    netProfit: 0,
    profitMargin: 0,
    cashOnHand: 0,
    accountsReceivable: 0,
    accountsPayable: 0,
    salesCount: 0,
    avgSalePrice: 0,
    inventoryValue: 0,
    revenueChange: 0,
    profitChange: 0,
    expenseChange: 0
  });

  const [revenueBreakdown, setRevenueBreakdown] = useState([]);
  const [expenseBreakdown, setExpenseBreakdown] = useState([]);
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [dealerOverview, setDealerOverview] = useState(null);

  // Financial reports state
  const [activeReport, setActiveReport] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);

  // Fetch dashboard data
  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true);
      setError(null);

      try {
        // Calculate date range for API calls
        const endDate = new Date();
        const startDate = new Date();

        switch (dateRange) {
          case 'week':
            startDate.setDate(endDate.getDate() - 7);
            break;
          case 'month':
            startDate.setMonth(endDate.getMonth() - 1);
            break;
          case 'quarter':
            startDate.setMonth(endDate.getMonth() - 3);
            break;
          case 'year':
            startDate.setFullYear(endDate.getFullYear() - 1);
            break;
          default:
            startDate.setMonth(endDate.getMonth() - 1);
        }

        // Fetch data in parallel (dealer overview is optional; don't fail the rest if it errors)
        const [profitLossRes, balanceSheetRes, salesRes, watchesRes, dashboardRes] = await Promise.all([
          accountingAPI.getProfitAndLoss({
            start_date: startDate.toISOString().split('T')[0],
            end_date: endDate.toISOString().split('T')[0]
          }),
          accountingAPI.getBalanceSheet({ as_of: endDate.toISOString().split('T')[0] }),
          salesAPI.getAll(),
          watchAPI.getAll(),
          accountingAPI.getDashboard().catch(() => ({ data: null }))
        ]);
        if (dashboardRes?.data) setDealerOverview(dashboardRes.data);

        const profitLoss = profitLossRes.data;
        const balanceSheet = balanceSheetRes.data;
        const sales = salesRes.data.sales || salesRes.data; // Handle both {sales: []} and []
        const watches = watchesRes.data.watches || watchesRes.data; // Handle both {watches: []} and []

        // Calculate metrics from 10-K formatted P&L
        const totalRevenue = profitLoss.net_sales?.total_net_sales || 0;
        const totalExpenses = profitLoss.operating_expenses?.total_operating_expenses || 0;
        const netProfit = profitLoss.net_income?.amount || 0;
        const profitMargin = profitLoss.net_income?.percentage || 0;
        const grossProfit = profitLoss.gross_margin?.amount || 0;
        const grossMargin = profitLoss.gross_margin?.percentage || 0;

        // Get balance sheet values from 10-K formatted response (Current Assets/Liabilities)
        const cashOnHand = balanceSheet.current_assets?.cash_and_cash_equivalents || 0;
        const accountsReceivable = balanceSheet.current_assets?.accounts_receivable_net || 0;
        const accountsPayable = balanceSheet.current_liabilities?.accounts_payable || 0;
        const inventoryValue = balanceSheet.current_assets?.inventories || 0;

        // Calculate sales metrics (ensure sales is an array)
        const salesArray = Array.isArray(sales) ? sales : [];
        const salesCount = salesArray.length;
        const totalSalesAmountRaw = salesArray.reduce((sum, s) => {
          const amt = Number(s.sale_price) || Number(s.final_price);
          return sum + (typeof amt === 'number' && !Number.isNaN(amt) ? amt : 0);
        }, 0);
        const totalSalesAmount = typeof totalSalesAmountRaw === 'number' && !Number.isNaN(totalSalesAmountRaw) ? totalSalesAmountRaw : 0;
        const avgSalePrice = salesCount > 0 ? totalSalesAmount / salesCount : 0;
        const safeAvgSalePrice = typeof avgSalePrice === 'number' && !Number.isNaN(avgSalePrice) ? avgSalePrice : 0;

        setMetrics({
          totalRevenue: Number(totalRevenue) || 0,
          totalExpenses: Number(totalExpenses) || 0,
          netProfit: Number(netProfit) || 0,
          profitMargin: typeof profitMargin === 'number' && !Number.isNaN(profitMargin) ? profitMargin : (parseFloat(profitMargin) || 0),
          grossProfit,
          grossMargin: parseFloat(grossMargin),
          cashOnHand: Number(cashOnHand) || 0,
          accountsReceivable: Number(accountsReceivable) || 0,
          accountsPayable: Number(accountsPayable) || 0,
          salesCount,
          avgSalePrice: safeAvgSalePrice,
          inventoryValue: Number(inventoryValue) || 0,
          revenueChange: 0, // TODO: Calculate based on previous period
          profitChange: 0,
          expenseChange: 0
        });

        // Build revenue breakdown from sales (backend returns "Retail", "Wholesale", "Consignment")
        const saleAmount = (s) => {
          const n = Number(s.sale_price) || Number(s.final_price);
          return typeof n === 'number' && !Number.isNaN(n) ? n : 0;
        };
        const retailSales = salesArray.filter(s => (s.sale_type || '').toUpperCase() === 'RETAIL' || (s.sale_type || '') === 'Retail').reduce((sum, s) => sum + saleAmount(s), 0);
        const wholesaleSales = salesArray.filter(s => (s.sale_type || '').toUpperCase() === 'WHOLESALE' || (s.sale_type || '') === 'Wholesale').reduce((sum, s) => sum + saleAmount(s), 0);
        const consignmentSales = salesArray.filter(s => (s.sale_type || '').toUpperCase() === 'CONSIGNMENT' || (s.sale_type || '') === 'Consignment').reduce((sum, s) => sum + saleAmount(s), 0);

        const revenueTotal = retailSales + wholesaleSales + consignmentSales;

        setRevenueBreakdown([
          {
            type: 'Retail Sales',
            amount: retailSales,
            percentage: revenueTotal > 0 ? (retailSales / revenueTotal * 100).toFixed(1) : 0,
            color: 'bg-blue-500'
          },
          {
            type: 'Wholesale Sales',
            amount: wholesaleSales,
            percentage: revenueTotal > 0 ? (wholesaleSales / revenueTotal * 100).toFixed(1) : 0,
            color: 'bg-purple-500'
          },
          {
            type: 'Consignment',
            amount: consignmentSales,
            percentage: revenueTotal > 0 ? (consignmentSales / revenueTotal * 100).toFixed(1) : 0,
            color: 'bg-green-500'
          }
        ]);

        // Build expense breakdown from 10-K formatted P&L
        if (profitLoss.operating_expenses?.detail_by_category) {
          const expenseTotal = Object.values(profitLoss.operating_expenses.detail_by_category).reduce((sum, val) => sum + (Number(val) || 0), 0);

          setExpenseBreakdown(
            Object.entries(profitLoss.operating_expenses.detail_by_category).map(([category, amount]) => ({
              category,
              amount: Number(amount) || 0,
              percentage: expenseTotal > 0 ? ((Number(amount) || 0) / expenseTotal * 100).toFixed(1) : 0
            }))
          );
        }

        // Recent transactions from sales (last 5) – full watch + serial for journals/audit
        setRecentTransactions(
          salesArray
            .slice(-5)
            .reverse()
            .map(sale => ({
              id: sale.id,
              invoice_number: sale.invoice_number,
              date: sale.sale_date,
              type: sale.sale_type,
              watch_brand: sale.watch_brand,
              watch_model: sale.watch_model,
              watch_reference: sale.watch_reference,
              watch_serial_number: sale.watch_serial_number,
              description: [sale.watch_brand || sale.watch?.brand, sale.watch_model || sale.watch?.model].filter(Boolean).join(' ') || 'Watch',
              amount: sale.sale_price || 0,
              profit: sale.profit || 0
            }))
        );

      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError('Failed to load dashboard data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [dateRange]);

  // Fetch financial report data
  const fetchReport = async (reportType) => {
    setReportLoading(true);
    setActiveReport(reportType);
    setError(null);

    try {
      const endDate = new Date();
      const startDate = new Date();

      // Use same date range as dashboard
      switch (dateRange) {
        case 'week':
          startDate.setDate(endDate.getDate() - 7);
          break;
        case 'month':
          startDate.setMonth(endDate.getMonth() - 1);
          break;
        case 'quarter':
          startDate.setMonth(endDate.getMonth() - 3);
          break;
        case 'year':
          startDate.setFullYear(endDate.getFullYear() - 1);
          break;
        default:
          startDate.setMonth(endDate.getMonth() - 1);
      }

      const params = {
        start_date: startDate.toISOString().split('T')[0],
        end_date: endDate.toISOString().split('T')[0]
      };

      let response;
      switch (reportType) {
        case 'profit-loss':
          response = await accountingAPI.getProfitAndLoss(params);
          break;
        case 'balance-sheet':
          response = await accountingAPI.getBalanceSheet({ as_of: params.end_date });
          break;
        case 'cash-flow':
          response = await accountingAPI.getCashFlow(params);
          break;
        case 'trial-balance':
          response = await accountingAPI.getTrialBalance({ as_of: params.end_date });
          break;
        case 'general-ledger':
          response = await accountingAPI.getGeneralLedger(params);
          break;
        case 'journal-entries':
          response = await accountingAPI.getJournalEntries(params);
          break;
        default:
          throw new Error('Unknown report type');
      }

      setReportData(response.data);
    } catch (err) {
      console.error(`Error fetching ${reportType}:`, err);
      setError(`Failed to load ${reportType}. Please try again.`);
    } finally {
      setReportLoading(false);
    }
  };

  // Export report as PDF
  const exportReportPDF = async () => {
    try {
      const endDate = new Date();
      const startDate = new Date();

      // Use same date range as dashboard
      switch (dateRange) {
        case 'week':
          startDate.setDate(endDate.getDate() - 7);
          break;
        case 'month':
          startDate.setMonth(endDate.getMonth() - 1);
          break;
        case 'quarter':
          startDate.setMonth(endDate.getMonth() - 3);
          break;
        case 'year':
          startDate.setFullYear(endDate.getFullYear() - 1);
          break;
        default:
          startDate.setMonth(endDate.getMonth() - 1);
      }

      const params = {
        start_date: startDate.toISOString().split('T')[0],
        end_date: endDate.toISOString().split('T')[0],
        as_of: endDate.toISOString().split('T')[0]
      };

      let response;
      switch (activeReport) {
        case 'profit-loss':
          response = await accountingAPI.exportProfitAndLossPDF(params);
          break;
        case 'balance-sheet':
          response = await accountingAPI.exportBalanceSheetPDF(params);
          break;
        default:
          alert('PDF export not available for this report yet');
          return;
      }

      // Create blob and download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${activeReport}-${params.end_date}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting PDF:', err);
      alert('Failed to export PDF. This feature may not be implemented on the backend yet.');
    }
  };

  // Render formatted report based on type
  const renderFormattedReport = () => {
    if (!reportData) return null;

    switch (activeReport) {
      case 'profit-loss':
        return (
          <div className="space-y-6">
            {/* Report Title - 10-K Style */}
            <div className="text-center pb-4 border-b-2 border-gray-300">
              <h3 className="text-2xl font-bold text-gray-900">{reportData.report_title || 'CONSOLIDATED STATEMENT OF OPERATIONS'}</h3>
              <p className="text-sm text-gray-600 mt-1">{reportData.subtitle || `Period: ${new Date(reportData.period?.start_date).toLocaleDateString()} - ${new Date(reportData.period?.end_date).toLocaleDateString()}`}</p>
            </div>

            {/* Net Sales */}
            <div className="bg-white rounded-lg border-2 border-gray-200 p-6">
              <h4 className="text-lg font-bold text-gray-900 mb-4">Net Sales</h4>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-700">Products (Retail & Wholesale)</span>
                  <span className="font-bold text-green-600">${(reportData.net_sales?.products || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-700">Services (Consignment Commissions)</span>
                  <span className="font-bold text-green-600">${(reportData.net_sales?.services || 0).toLocaleString()}</span>
                </div>
                <div className="border-t-2 border-gray-300 pt-3 mt-3 flex justify-between items-center">
                  <span className="font-bold text-gray-900">Total Net Sales</span>
                  <span className="font-bold text-2xl text-green-600">${(reportData.net_sales?.total_net_sales || 0).toLocaleString()}</span>
                </div>
              </div>
            </div>

            {/* Cost of Sales */}
            <div className="bg-white rounded-lg border-2 border-gray-200 p-6">
              <h4 className="text-lg font-bold text-gray-900 mb-4">Cost of Sales</h4>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-gray-700">Cost of Products</span>
                  <span className="font-bold text-red-600">${(reportData.cost_of_sales?.cost_of_products || 0).toLocaleString()}</span>
                </div>
                <div className="border-t border-gray-300 pt-2 mt-2 flex justify-between items-center">
                  <span className="font-bold text-gray-900">Total Cost of Sales</span>
                  <span className="font-bold text-red-600">${(reportData.cost_of_sales?.total_cost_of_sales || 0).toLocaleString()}</span>
                </div>
              </div>
            </div>

            {/* Gross Margin */}
            <div className="bg-gradient-to-r from-emerald-50 to-green-50 rounded-lg border-2 border-emerald-300 p-6">
              <h4 className="text-lg font-bold text-gray-900 mb-4">Gross Margin</h4>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-gray-700">Amount</span>
                  <span className="font-bold text-2xl text-emerald-600">${(reportData.gross_margin?.amount || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-700">Margin %</span>
                  <span className="font-bold text-emerald-600">{(reportData.gross_margin?.percentage || 0).toFixed(2)}%</span>
                </div>
              </div>
            </div>

            {/* Operating Expenses */}
            <div className="bg-white rounded-lg border-2 border-gray-200 p-6">
              <h4 className="text-lg font-bold text-gray-900 mb-4">Operating Expenses</h4>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-gray-700">Selling, General & Administrative</span>
                  <span className="font-bold text-red-600">${(reportData.operating_expenses?.selling_general_administrative || 0).toLocaleString()}</span>
                </div>
                {reportData.operating_expenses?.detail_by_category && Object.keys(reportData.operating_expenses.detail_by_category).length > 0 && (
                  <div className="ml-4 mt-2 space-y-1">
                    {Object.entries(reportData.operating_expenses.detail_by_category).map(([category, amount]) => (
                      <div key={category} className="flex justify-between items-center text-sm">
                        <span className="text-gray-600">• {category}</span>
                        <span className="text-gray-700">${(Number(amount) || 0).toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                )}
                <div className="border-t-2 border-gray-300 pt-3 mt-3 flex justify-between items-center">
                  <span className="font-bold text-gray-900">Total Operating Expenses</span>
                  <span className="font-bold text-xl text-red-600">${(reportData.operating_expenses?.total_operating_expenses || 0).toLocaleString()}</span>
                </div>
              </div>
            </div>

            {/* Operating Income */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border-2 border-blue-300 p-6">
              <h4 className="text-lg font-bold text-gray-900 mb-4">Operating Income</h4>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-gray-700">Amount</span>
                  <span className="font-bold text-2xl text-blue-600">${(reportData.operating_income?.amount || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-700">Operating Margin %</span>
                  <span className="font-bold text-blue-600">{(reportData.operating_income?.percentage || 0).toFixed(2)}%</span>
                </div>
              </div>
            </div>

            {/* Other Income/(Expense) */}
            <div className="bg-white rounded-lg border-2 border-gray-200 p-6">
              <h4 className="text-lg font-bold text-gray-900 mb-4">Other Income/(Expense), net</h4>
              <div className="flex justify-between items-center">
                <span className="text-gray-700">Total Other Income/(Expense), net</span>
                <span className="font-bold text-gray-900">${(reportData.other_income_expense_net?.total_other_income_expense_net || 0).toLocaleString()}</span>
              </div>
            </div>

            {/* Income Before Taxes */}
            <div className="bg-white rounded-lg border-2 border-gray-200 p-6">
              <h4 className="text-lg font-bold text-gray-900 mb-4">Income Before Provision for Income Taxes</h4>
              <div className="flex justify-between items-center">
                <span className="font-bold text-gray-900">Amount</span>
                <span className="font-bold text-xl text-gray-900">${(reportData.income_before_provision_for_income_taxes || 0).toLocaleString()}</span>
              </div>
            </div>

            {/* Tax Provision */}
            <div className="bg-white rounded-lg border-2 border-gray-200 p-6">
              <h4 className="text-lg font-bold text-gray-900 mb-4">Provision for Income Taxes</h4>
              <div className="flex justify-between items-center">
                <span className="text-gray-700">Tax Provision</span>
                <span className="font-bold text-red-600">${(reportData.provision_for_income_taxes || 0).toLocaleString()}</span>
              </div>
            </div>

            {/* Net Income */}
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-6 text-white shadow-xl">
              <h4 className="text-lg font-bold mb-4">Net Income</h4>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span>Amount</span>
                  <span className="font-bold text-3xl">${(reportData.net_income?.amount || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span>Net Margin %</span>
                  <span className="font-bold text-xl">{(reportData.net_income?.percentage || 0).toFixed(2)}%</span>
                </div>
              </div>
            </div>
          </div>
        );

      case 'balance-sheet':
        return (
          <div className="space-y-6">
            {/* Report Title - 10-K Style */}
            <div className="text-center pb-4 border-b-2 border-gray-300">
              <h3 className="text-2xl font-bold text-gray-900">
                {reportData.report_title || 'CONSOLIDATED BALANCE SHEET'}
              </h3>
              {reportData.subtitle && (
                <p className="text-sm text-gray-600 mt-1">{reportData.subtitle}</p>
              )}
            </div>

            {/* ASSETS */}
            <div className="bg-white rounded-lg border-2 border-green-200 p-6">
              <h4 className="text-xl font-bold text-green-900 mb-4 uppercase">Assets</h4>

              {/* Current Assets */}
              {reportData.current_assets && (
                <div className="mb-6">
                  <h5 className="font-bold text-gray-900 mb-3">Current Assets:</h5>
                  <div className="space-y-2 ml-4">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">Cash and Cash Equivalents</span>
                      <span className="font-semibold text-green-600">
                        ${(reportData.current_assets.cash_and_cash_equivalents || 0).toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">Accounts Receivable, Net</span>
                      <span className="font-semibold text-green-600">
                        ${(reportData.current_assets.accounts_receivable_net || 0).toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">Inventories</span>
                      <span className="font-semibold text-green-600">
                        ${(reportData.current_assets.inventories || 0).toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">Other Current Assets</span>
                      <span className="font-semibold text-green-600">
                        ${(reportData.current_assets.other_current_assets || 0).toLocaleString()}
                      </span>
                    </div>
                    <div className="border-t border-gray-300 pt-2 mt-2 flex justify-between items-center">
                      <span className="font-bold text-gray-900">Total Current Assets</span>
                      <span className="font-bold text-green-700">
                        ${(reportData.current_assets.total_current_assets || 0).toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Non-Current Assets */}
              {reportData.non_current_assets && (
                <div className="mb-6">
                  <h5 className="font-bold text-gray-900 mb-3">Non-Current Assets:</h5>
                  <div className="space-y-2 ml-4">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">Property, Plant and Equipment, Net</span>
                      <span className="font-semibold text-green-600">
                        ${(reportData.non_current_assets.property_plant_equipment_net || 0).toLocaleString()}
                      </span>
                    </div>
                    <div className="border-t border-gray-300 pt-2 mt-2 flex justify-between items-center">
                      <span className="font-bold text-gray-900">Total Non-Current Assets</span>
                      <span className="font-bold text-green-700">
                        ${(reportData.non_current_assets.total_non_current_assets || 0).toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Total Assets */}
              <div className="border-t-2 border-green-400 pt-3 mt-3 flex justify-between items-center">
                <span className="font-bold text-xl text-gray-900">TOTAL ASSETS</span>
                <span className="font-bold text-2xl text-green-600">
                  ${(reportData.total_assets || 0).toLocaleString()}
                </span>
              </div>
            </div>

            {/* LIABILITIES */}
            <div className="bg-white rounded-lg border-2 border-red-200 p-6">
              <h4 className="text-xl font-bold text-red-900 mb-4 uppercase">Liabilities</h4>

              {/* Current Liabilities */}
              {reportData.current_liabilities && (
                <div className="mb-6">
                  <h5 className="font-bold text-gray-900 mb-3">Current Liabilities:</h5>
                  <div className="space-y-2 ml-4">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">Accounts Payable</span>
                      <span className="font-semibold text-red-600">
                        ${(reportData.current_liabilities.accounts_payable || 0).toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">Accrued Expenses</span>
                      <span className="font-semibold text-red-600">
                        ${(reportData.current_liabilities.accrued_expenses || 0).toLocaleString()}
                      </span>
                    </div>
                    <div className="border-t border-gray-300 pt-2 mt-2 flex justify-between items-center">
                      <span className="font-bold text-gray-900">Total Current Liabilities</span>
                      <span className="font-bold text-red-700">
                        ${(reportData.current_liabilities.total_current_liabilities || 0).toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Non-Current Liabilities */}
              {reportData.non_current_liabilities && (
                <div className="mb-6">
                  <h5 className="font-bold text-gray-900 mb-3">Non-Current Liabilities:</h5>
                  <div className="space-y-2 ml-4">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">Long-Term Debt</span>
                      <span className="font-semibold text-red-600">
                        ${(reportData.non_current_liabilities.long_term_debt || 0).toLocaleString()}
                      </span>
                    </div>
                    <div className="border-t border-gray-300 pt-2 mt-2 flex justify-between items-center">
                      <span className="font-bold text-gray-900">Total Non-Current Liabilities</span>
                      <span className="font-bold text-red-700">
                        ${(reportData.non_current_liabilities.total_non_current_liabilities || 0).toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Total Liabilities */}
              <div className="border-t-2 border-red-400 pt-3 mt-3 flex justify-between items-center">
                <span className="font-bold text-xl text-gray-900">TOTAL LIABILITIES</span>
                <span className="font-bold text-2xl text-red-600">
                  ${(reportData.total_liabilities || 0).toLocaleString()}
                </span>
              </div>
            </div>

            {/* STOCKHOLDERS' EQUITY */}
            {reportData.stockholders_equity && (
              <div className="bg-white rounded-lg border-2 border-blue-200 p-6">
                <h4 className="text-xl font-bold text-blue-900 mb-4 uppercase">Stockholders' Equity</h4>
                <div className="space-y-2 ml-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-700">Common Stock</span>
                    <span className="font-semibold text-blue-600">
                      ${(reportData.stockholders_equity.common_stock || 0).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-700">Retained Earnings</span>
                    <span className="font-semibold text-blue-600">
                      ${(reportData.stockholders_equity.retained_earnings || 0).toLocaleString()}
                    </span>
                  </div>
                  <div className="border-t-2 border-blue-400 pt-3 mt-3 flex justify-between items-center">
                    <span className="font-bold text-xl text-gray-900">TOTAL STOCKHOLDERS' EQUITY</span>
                    <span className="font-bold text-2xl text-blue-600">
                      ${(reportData.stockholders_equity.total_stockholders_equity || 0).toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Total Liabilities + Equity (Accounting Equation Validation) */}
            <div className="bg-gradient-to-r from-purple-500 to-blue-600 rounded-lg p-6 text-white shadow-xl">
              <h4 className="text-lg font-bold mb-4">TOTAL LIABILITIES AND STOCKHOLDERS' EQUITY</h4>
              <div className="text-3xl font-bold mb-2">
                ${((reportData.total_liabilities || 0) + (reportData.stockholders_equity?.total_stockholders_equity || 0)).toLocaleString()}
              </div>
              <div className="text-sm opacity-90">
                {reportData.is_balanced ? '✓ Balance Sheet is balanced (Assets = Liabilities + Equity)' : '⚠ Balance Sheet does not balance'}
              </div>
            </div>
          </div>
        );

      case 'journal-entries':
        const entries = reportData.entries || reportData || [];
        return (
          <div className="space-y-4">
            {Array.isArray(entries) && entries.length > 0 ? (
              entries.map((entry, index) => (
                <div key={index} className="bg-white rounded-lg border-2 border-gray-200 p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <div className="font-bold text-gray-900">Entry #{entry.id || index + 1}</div>
                      <div className="text-sm text-gray-600">{entry.entry_date ? new Date(entry.entry_date).toLocaleDateString() : 'N/A'}</div>
                    </div>
                    <div className={`px-3 py-1 rounded-full text-xs font-semibold ${entry.is_posted ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                      {entry.is_posted ? 'Posted' : 'Draft'}
                    </div>
                  </div>
                  {entry.description && <div className="text-sm text-gray-700 mb-2">{entry.description}</div>}
                  {entry.line_items && entry.line_items.length > 0 && (
                    <div className="mt-3 space-y-2 border-t pt-3">
                      {entry.line_items.map((line, i) => (
                        <div key={i} className="flex justify-between text-sm">
                          <span className="text-gray-700">{line.account_name || line.account}</span>
                          <div className="flex gap-4">
                            <span className="text-green-600">{line.debit_amount ? `$${Number(line.debit_amount).toLocaleString()}` : '-'}</span>
                            <span className="text-red-600">{line.credit_amount ? `$${Number(line.credit_amount).toLocaleString()}` : '-'}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <button
                      onClick={() => navigate('/accounting/general-journal')}
                      className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                    >
                      View in General Journal →
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="bg-gray-50 rounded-lg p-8 text-center text-gray-500">
                No journal entries found for this period
              </div>
            )}
          </div>
        );

      case 'cash-flow':
        return (
          <div className="space-y-6">
            {/* Report Title - 10-K Style */}
            <div className="text-center pb-4 border-b-2 border-gray-300">
              <h3 className="text-2xl font-bold text-gray-900">
                {reportData.report_title || 'CONSOLIDATED STATEMENT OF CASH FLOWS'}
              </h3>
              {reportData.subtitle && (
                <p className="text-sm text-gray-600 mt-1">{reportData.subtitle}</p>
              )}
              {reportData.method && (
                <p className="text-xs text-gray-500 mt-1">({reportData.method})</p>
              )}
            </div>

            {/* Operating Activities - Indirect Method */}
            <div className="bg-white rounded-lg border-2 border-blue-200 p-6">
              <h4 className="text-xl font-bold text-blue-900 mb-4 uppercase">Cash Flows from Operating Activities</h4>
              {reportData.operating_activities ? (
                <div className="space-y-3">
                  {/* Net Income (starting point for indirect method) */}
                  <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                    <span className="font-bold text-gray-900">Net Income</span>
                    <span className="font-bold text-blue-600">
                      ${(reportData.operating_activities.net_income || 0).toLocaleString()}
                    </span>
                  </div>

                  {/* Adjustments to Reconcile */}
                  {reportData.operating_activities.adjustments_to_reconcile && (
                    <div className="ml-4 space-y-2">
                      <div className="font-semibold text-gray-700 mt-2">Adjustments to reconcile net income to net cash:</div>

                      {/* Depreciation and Amortization */}
                      {reportData.operating_activities.adjustments_to_reconcile.depreciation_and_amortization !== undefined && (
                        <div className="flex justify-between items-center ml-4">
                          <span className="text-gray-700">Depreciation and Amortization</span>
                          <span className="font-semibold text-green-600">
                            ${(reportData.operating_activities.adjustments_to_reconcile.depreciation_and_amortization || 0).toLocaleString()}
                          </span>
                        </div>
                      )}

                      {/* Changes in Operating Assets and Liabilities */}
                      {reportData.operating_activities.adjustments_to_reconcile.changes_in_operating_assets_liabilities && (
                        <div className="mt-3">
                          <div className="text-sm text-gray-600 mb-2">Changes in operating assets and liabilities:</div>
                          <div className="ml-4 space-y-2">
                            {reportData.operating_activities.adjustments_to_reconcile.changes_in_operating_assets_liabilities.accounts_receivable !== undefined && (
                              <div className="flex justify-between items-center">
                                <span className="text-gray-700 text-sm">Accounts Receivable</span>
                                <span className={`font-semibold text-sm ${
                                  reportData.operating_activities.adjustments_to_reconcile.changes_in_operating_assets_liabilities.accounts_receivable >= 0
                                    ? 'text-green-600'
                                    : 'text-red-600'
                                }`}>
                                  ${(reportData.operating_activities.adjustments_to_reconcile.changes_in_operating_assets_liabilities.accounts_receivable || 0).toLocaleString()}
                                </span>
                              </div>
                            )}
                            {reportData.operating_activities.adjustments_to_reconcile.changes_in_operating_assets_liabilities.inventories !== undefined && (
                              <div className="flex justify-between items-center">
                                <span className="text-gray-700 text-sm">Inventories</span>
                                <span className={`font-semibold text-sm ${
                                  reportData.operating_activities.adjustments_to_reconcile.changes_in_operating_assets_liabilities.inventories >= 0
                                    ? 'text-green-600'
                                    : 'text-red-600'
                                }`}>
                                  ${(reportData.operating_activities.adjustments_to_reconcile.changes_in_operating_assets_liabilities.inventories || 0).toLocaleString()}
                                </span>
                              </div>
                            )}
                            {reportData.operating_activities.adjustments_to_reconcile.changes_in_operating_assets_liabilities.accounts_payable !== undefined && (
                              <div className="flex justify-between items-center">
                                <span className="text-gray-700 text-sm">Accounts Payable</span>
                                <span className={`font-semibold text-sm ${
                                  reportData.operating_activities.adjustments_to_reconcile.changes_in_operating_assets_liabilities.accounts_payable >= 0
                                    ? 'text-green-600'
                                    : 'text-red-600'
                                }`}>
                                  ${(reportData.operating_activities.adjustments_to_reconcile.changes_in_operating_assets_liabilities.accounts_payable || 0).toLocaleString()}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Net Cash from Operating Activities */}
                  <div className="border-t-2 border-blue-400 pt-3 mt-3 flex justify-between items-center">
                    <span className="font-bold text-gray-900">Net Cash Provided by Operating Activities</span>
                    <span className="font-bold text-xl text-blue-600">
                      ${(reportData.operating_activities.net_cash_provided_by_operating_activities || 0).toLocaleString()}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="text-gray-500 italic">No operating activities</div>
              )}
            </div>

            {/* Investing Activities */}
            <div className="bg-white rounded-lg border-2 border-purple-200 p-6">
              <h4 className="text-xl font-bold text-purple-900 mb-4 uppercase">Cash Flows from Investing Activities</h4>
              {reportData.investing_activities ? (
                <div className="space-y-3">
                  {reportData.investing_activities.payments_for_acquisition_of_property_equipment !== undefined && (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">Payments for Acquisition of Property and Equipment</span>
                      <span className="font-semibold text-red-600">
                        ${(reportData.investing_activities.payments_for_acquisition_of_property_equipment || 0).toLocaleString()}
                      </span>
                    </div>
                  )}
                  <div className="border-t-2 border-purple-400 pt-3 mt-3 flex justify-between items-center">
                    <span className="font-bold text-gray-900">Net Cash Used in Investing Activities</span>
                    <span className={`font-bold text-xl ${
                      (reportData.investing_activities.net_cash_used_in_investing_activities || 0) >= 0
                        ? 'text-purple-600'
                        : 'text-red-600'
                    }`}>
                      ${(reportData.investing_activities.net_cash_used_in_investing_activities || 0).toLocaleString()}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="text-gray-500 italic">No investing activities</div>
              )}
            </div>

            {/* Financing Activities */}
            <div className="bg-white rounded-lg border-2 border-amber-200 p-6">
              <h4 className="text-xl font-bold text-amber-900 mb-4 uppercase">Cash Flows from Financing Activities</h4>
              {reportData.financing_activities ? (
                <div className="space-y-3">
                  {reportData.financing_activities.proceeds_from_issuance_of_debt !== undefined && (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">Proceeds from Issuance of Debt</span>
                      <span className="font-semibold text-green-600">
                        ${(reportData.financing_activities.proceeds_from_issuance_of_debt || 0).toLocaleString()}
                      </span>
                    </div>
                  )}
                  <div className="border-t-2 border-amber-400 pt-3 mt-3 flex justify-between items-center">
                    <span className="font-bold text-gray-900">Net Cash Provided by Financing Activities</span>
                    <span className={`font-bold text-xl ${
                      (reportData.financing_activities.net_cash_provided_by_financing_activities || 0) >= 0
                        ? 'text-amber-600'
                        : 'text-red-600'
                    }`}>
                      ${(reportData.financing_activities.net_cash_provided_by_financing_activities || 0).toLocaleString()}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="text-gray-500 italic">No financing activities</div>
              )}
            </div>

            {/* Net Change in Cash */}
            <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg p-6 text-white shadow-xl">
              <h4 className="text-lg font-bold mb-4 uppercase">Net Increase (Decrease) in Cash and Cash Equivalents</h4>
              <div className="text-3xl font-bold mb-4">
                ${(reportData.net_increase_decrease_in_cash || 0).toLocaleString()}
              </div>
              <div className="space-y-2 text-sm border-t border-white/30 pt-4">
                <div className="flex justify-between items-center">
                  <span>Cash and Equivalents, Beginning of Period</span>
                  <span className="font-semibold">
                    ${(reportData.cash_and_equivalents_beginning_of_period || 0).toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between items-center font-bold text-base">
                  <span>Cash and Equivalents, End of Period</span>
                  <span className="text-lg">
                    ${(reportData.cash_and_equivalents_end_of_period || 0).toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
          </div>
        );

      case 'trial-balance':
        const accounts = reportData.accounts || reportData || [];
        return (
          <div className="space-y-6">
            {/* As Of Date */}
            {reportData.as_of && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="text-sm font-semibold text-blue-900">
                  As of: {new Date(reportData.as_of).toLocaleDateString()}
                </div>
              </div>
            )}

            {/* Trial Balance Table */}
            <div className="bg-white rounded-lg border-2 border-gray-200 p-6">
              <h4 className="text-lg font-bold text-gray-900 mb-4">Trial Balance</h4>

              {Array.isArray(accounts) && accounts.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b-2 border-gray-300">
                        <th className="text-left py-3 px-4 font-bold text-gray-900">Account</th>
                        <th className="text-right py-3 px-4 font-bold text-green-900">Debit</th>
                        <th className="text-right py-3 px-4 font-bold text-red-900">Credit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {accounts.map((account, index) => (
                        <tr key={index} className="border-b border-gray-200 hover:bg-gray-50">
                          <td className="py-3 px-4 text-gray-700">{account.name || account.account_name}</td>
                          <td className="py-3 px-4 text-right font-semibold text-green-600">
                            {(account.debit_balance ?? account.debit) != null && (Number(account.debit_balance ?? account.debit) || 0) !== 0 ? `$${Number(account.debit_balance ?? account.debit).toLocaleString()}` : '-'}
                          </td>
                          <td className="py-3 px-4 text-right font-semibold text-red-600">
                            {(account.credit_balance ?? account.credit) != null && (Number(account.credit_balance ?? account.credit) || 0) !== 0 ? `$${Number(account.credit_balance ?? account.credit).toLocaleString()}` : '-'}
                          </td>
                        </tr>
                      ))}
                      <tr className="border-t-2 border-gray-300 font-bold bg-gray-50">
                        <td className="py-3 px-4 text-gray-900">TOTALS</td>
                        <td className="py-3 px-4 text-right text-green-700">
                          ${(reportData.total_debits || 0).toLocaleString()}
                        </td>
                        <td className="py-3 px-4 text-right text-red-700">
                          ${(reportData.total_credits || 0).toLocaleString()}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-gray-500 italic text-center py-8">No accounts found</div>
              )}
            </div>

            {/* Balance Check */}
            <div className={`p-4 rounded-lg ${Math.abs((reportData.total_debits || 0) - (reportData.total_credits || 0)) < 0.01 ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
              <div className={`text-sm font-semibold ${Math.abs((reportData.total_debits || 0) - (reportData.total_credits || 0)) < 0.01 ? 'text-green-900' : 'text-red-900'}`}>
                {Math.abs((reportData.total_debits || 0) - (reportData.total_credits || 0)) < 0.01
                  ? '✓ Trial Balance is balanced (Debits = Credits)'
                  : '⚠ Warning: Trial Balance does not balance'}
              </div>
            </div>
          </div>
        );

      case 'general-ledger':
        const ledgerAccounts = reportData.accounts || reportData || [];
        return (
          <div className="space-y-6">
            {/* Period */}
            {reportData.period && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="text-sm font-semibold text-blue-900">
                  Period: {new Date(reportData.period.start_date).toLocaleDateString()} - {new Date(reportData.period.end_date).toLocaleDateString()}
                </div>
              </div>
            )}

            {/* General Ledger Accounts */}
            {Array.isArray(ledgerAccounts) && ledgerAccounts.length > 0 ? (
              ledgerAccounts.map((account, index) => (
                <div key={index} className="bg-white rounded-lg border-2 border-gray-200 p-6">
                  <div className="mb-4">
                    <h4 className="text-lg font-bold text-gray-900">{account.name || account.account_name}</h4>
                    <div className="text-sm text-gray-600">Account #{account.account_number || account.id}</div>
                  </div>

                  {account.transactions && account.transactions.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-gray-300">
                            <th className="text-left py-2 px-2 font-semibold text-gray-700">Date</th>
                            <th className="text-left py-2 px-2 font-semibold text-gray-700">Description</th>
                            <th className="text-right py-2 px-2 font-semibold text-green-700">Debit</th>
                            <th className="text-right py-2 px-2 font-semibold text-red-700">Credit</th>
                            <th className="text-right py-2 px-2 font-semibold text-gray-700">Balance</th>
                          </tr>
                        </thead>
                        <tbody>
                          {account.transactions.map((txn, i) => (
                            <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                              <td className="py-2 px-2 text-gray-700">
                                {txn.date ? new Date(txn.date).toLocaleDateString() : '-'}
                              </td>
                              <td className="py-2 px-2 text-gray-700">{txn.description || '-'}</td>
                              <td className="py-2 px-2 text-right text-green-600">
                                {txn.debit != null && Number(txn.debit) !== 0 ? `$${Number(txn.debit).toLocaleString()}` : '-'}
                              </td>
                              <td className="py-2 px-2 text-right text-red-600">
                                {txn.credit != null && Number(txn.credit) !== 0 ? `$${Number(txn.credit).toLocaleString()}` : '-'}
                              </td>
                              <td className="py-2 px-2 text-right font-semibold text-gray-900">
                                ${(Number(txn.balance) || 0).toLocaleString()}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-gray-500 italic text-center py-4">No transactions</div>
                  )}

                  <div className="mt-4 pt-4 border-t border-gray-200 flex justify-between">
                    <span className="font-semibold text-gray-700">Ending Balance:</span>
                    <span className="font-bold text-xl text-gray-900">
                      ${(Number(account.ending_balance) || 0).toLocaleString()}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className="bg-gray-50 rounded-lg p-8 text-center text-gray-500">
                No general ledger entries found
              </div>
            )}
          </div>
        );

      default:
        // Generic formatted display for other reports
        return (
          <div className="bg-white rounded-lg p-6">
            <pre className="text-sm font-mono text-gray-700 whitespace-pre-wrap overflow-x-auto">
              {JSON.stringify(reportData, null, 2)}
            </pre>
          </div>
        );
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="loading-screen">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-amber-500 animate-spin mx-auto mb-3" />
          <p className="text-slate-600">Loading accounting data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container space-y-6">
      <div className="page-header">
        <h1 className="page-title">Accounting</h1>
        <p className="page-subtitle">Your financial performance at a glance — no QuickBooks needed</p>
        {error && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
            {error}
          </div>
        )}
      </div>

        {/* Dealer overview: cash, receivables, payables, compliance, inventory, recent */}
        {dealerOverview && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5 mb-6">
            <h2 className="text-lg font-semibold text-slate-800 mb-4">At a glance</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
              <div className="p-3 rounded-xl bg-slate-50 border border-slate-100">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">{dealerOverview.cash?.label}</p>
                <p className="text-xl font-bold text-slate-900 mt-1">
                  {dealerOverview.cash?.balance != null ? `$${Number(dealerOverview.cash.balance).toLocaleString()}` : '—'}
                </p>
              </div>
              <div className="p-3 rounded-xl bg-emerald-50 border border-emerald-100">
                <p className="text-xs font-medium text-slate-600 uppercase tracking-wide">{dealerOverview.receivables?.label}</p>
                <p className="text-xl font-bold text-emerald-700 mt-1">${Number(dealerOverview.receivables?.total || 0).toLocaleString()}</p>
                {dealerOverview.receivables?.invoice_count > 0 && (
                  <p className="text-xs text-slate-500 mt-0.5">{dealerOverview.receivables.invoice_count} invoice(s)</p>
                )}
              </div>
              <div className="p-3 rounded-xl bg-amber-50 border border-amber-100">
                <p className="text-xs font-medium text-slate-600 uppercase tracking-wide">{dealerOverview.payables?.label}</p>
                <p className="text-xl font-bold text-amber-700 mt-1">${Number(dealerOverview.payables?.total || 0).toLocaleString()}</p>
                {dealerOverview.payables?.count > 0 && (
                  <p className="text-xs text-slate-500 mt-0.5">{dealerOverview.payables.count} bill(s)</p>
                )}
              </div>
              <div className="p-3 rounded-xl bg-violet-50 border border-violet-100">
                <p className="text-xs font-medium text-slate-600 uppercase tracking-wide">{dealerOverview.consignor_payouts?.label}</p>
                <p className="text-xl font-bold text-violet-700 mt-1">${Number(dealerOverview.consignor_payouts?.total || 0).toLocaleString()}</p>
                {dealerOverview.consignor_payouts?.count > 0 && (
                  <p className="text-xs text-slate-500 mt-0.5">{dealerOverview.consignor_payouts.count} payout(s)</p>
                )}
              </div>
              <div className="p-3 rounded-xl bg-sky-50 border border-sky-100">
                <p className="text-xs font-medium text-slate-600 uppercase tracking-wide">{dealerOverview.inventory?.label}</p>
                <p className="text-xl font-bold text-sky-700 mt-1">{dealerOverview.inventory?.piece_count ?? 0} pieces</p>
                <p className="text-xs text-slate-500 mt-0.5">${Number(dealerOverview.inventory?.total_value || 0).toLocaleString()} value</p>
              </div>
              <div className="p-3 rounded-xl bg-rose-50 border border-rose-100">
                <p className="text-xs font-medium text-slate-600 uppercase tracking-wide">{dealerOverview.compliance?.label}</p>
                <p className="text-sm font-semibold text-rose-700 mt-1">
                  {[dealerOverview.compliance?.resale_certs_expiring_soon, dealerOverview.compliance?.vendors_1099_count, (dealerOverview.compliance?.form_8300_alerts?.length || 0)].filter(Boolean).reduce((a, b) => a + b, 0)} to-do(s)
                </p>
              </div>
              <div className="p-3 rounded-xl bg-blue-50 border border-blue-100">
                <p className="text-xs font-medium text-slate-600 uppercase tracking-wide">{dealerOverview.recent_activity?.label}</p>
                <p className="text-xl font-bold text-blue-700 mt-1">{dealerOverview.recent_activity?.sales_last_7_days ?? 0} sales</p>
                <p className="text-xs text-slate-500 mt-0.5">${Number(dealerOverview.recent_activity?.revenue_last_7_days || 0).toLocaleString()} revenue</p>
              </div>
            </div>
          </div>
        )}

        {/* Date Range Selector */}
        <div className="bg-white rounded-2xl shadow-sm p-4 mb-6">
          <div className="flex gap-2">
            {['week', 'month', 'quarter', 'year'].map((range) => (
              <button
                key={range}
                onClick={() => setDateRange(range)}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  dateRange === range
                    ? 'bg-blue-600 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {range.charAt(0).toUpperCase() + range.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Key Metrics - Big Numbers */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          {/* Total Revenue */}
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl shadow-xl p-6 text-white">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-white/20 rounded-xl">
                <DollarSign className="w-6 h-6" />
              </div>
              <div className={`flex items-center gap-1 px-2 py-1 rounded-lg bg-white/20`}>
                <ArrowUpRight className="w-4 h-4" />
                <span className="text-sm font-semibold">{metrics.revenueChange}%</span>
              </div>
            </div>
            <div className="text-sm font-medium opacity-90 mb-1">Total Revenue</div>
            <div className="text-3xl font-bold">
              ${(Number(metrics.totalRevenue) || 0).toLocaleString()}
            </div>
          </div>

          {/* Net Profit */}
          <div className="bg-gradient-to-br from-emerald-500 to-green-600 rounded-2xl shadow-xl p-6 text-white">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-white/20 rounded-xl">
                <TrendingUp className="w-6 h-6" />
              </div>
              <div className="flex items-center gap-1 px-2 py-1 rounded-lg bg-white/20">
                <ArrowUpRight className="w-4 h-4" />
                <span className="text-sm font-semibold">{metrics.profitChange}%</span>
              </div>
            </div>
            <div className="text-sm font-medium opacity-90 mb-1">Net Profit</div>
            <div className="text-3xl font-bold">
              ${(Number(metrics.netProfit) || 0).toLocaleString()}
            </div>
            <div className="text-sm mt-2 opacity-90">
              {(Number(metrics.profitMargin) || 0).toFixed(1)}% margin
            </div>
          </div>

          {/* Total Expenses */}
          <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl shadow-xl p-6 text-white">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-white/20 rounded-xl">
                <Receipt className="w-6 h-6" />
              </div>
              <div className="flex items-center gap-1 px-2 py-1 rounded-lg bg-white/20">
                <ArrowDownRight className="w-4 h-4" />
                <span className="text-sm font-semibold">{Math.abs(metrics.expenseChange)}%</span>
              </div>
            </div>
            <div className="text-sm font-medium opacity-90 mb-1">Total Expenses</div>
            <div className="text-3xl font-bold">
              ${(Number(metrics.totalExpenses) || 0).toLocaleString()}
            </div>
          </div>

          {/* Cash on Hand */}
          <div className="bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl shadow-xl p-6 text-white">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-white/20 rounded-xl">
                <Wallet className="w-6 h-6" />
              </div>
            </div>
            <div className="text-sm font-medium opacity-90 mb-1">Cash on Hand</div>
            <div className="text-3xl font-bold">
              ${(Number(metrics.cashOnHand) || 0).toLocaleString()}
            </div>
            <div className="text-sm mt-2 opacity-90">
              Available now
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">

          {/* Revenue Breakdown - Visual */}
          <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">Revenue Breakdown</h2>
              <PieChart className="w-5 h-5 text-gray-400" />
            </div>

            <div className="space-y-4">
              {revenueBreakdown.map((item, index) => (
                <div key={index}>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-700">{item.type}</span>
                    <span className="text-sm font-bold text-gray-900">
                      ${(Number(item.amount) || 0).toLocaleString()}
                    </span>
                  </div>
                  <div className="relative h-3 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className={`absolute h-full ${item.color} rounded-full transition-all duration-500`}
                      style={{ width: `${item.percentage}%` }}
                    />
                  </div>
                  <div className="text-xs text-gray-500 mt-1">{item.percentage}% of total</div>
                </div>
              ))}
            </div>

            <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-gray-600 mb-1">Average Sale Price</div>
                  <div className="text-2xl font-bold text-gray-900">
                    ${(Number(metrics.avgSalePrice) || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm text-gray-600 mb-1">Sales This Period</div>
                  <div className="text-2xl font-bold text-gray-900">{metrics.salesCount}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-2xl shadow-sm p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Quick Actions</h2>

            <div className="space-y-3">
              <button
                onClick={() => navigate('/sales/record')}
                className="w-full flex items-center gap-3 p-4 bg-gradient-to-r from-blue-50 to-blue-100 hover:from-blue-100 hover:to-blue-200 rounded-xl transition-all group"
              >
                <div className="p-2 bg-blue-600 text-white rounded-lg group-hover:scale-110 transition-transform">
                  <DollarSign className="w-5 h-5" />
                </div>
                <div className="text-left">
                  <div className="font-semibold text-gray-900">Record Sale</div>
                  <div className="text-xs text-gray-600">Auto-creates accounting entry</div>
                </div>
              </button>

              <button
                onClick={() => navigate('/inventory')}
                className="w-full flex items-center gap-3 p-4 bg-gradient-to-r from-purple-50 to-purple-100 hover:from-purple-100 hover:to-purple-200 rounded-xl transition-all group"
              >
                <div className="p-2 bg-purple-600 text-white rounded-lg group-hover:scale-110 transition-transform">
                  <Package className="w-5 h-5" />
                </div>
                <div className="text-left">
                  <div className="font-semibold text-gray-900">View Inventory</div>
                  <div className="text-xs text-gray-600">Current stock & values</div>
                </div>
              </button>

              <button
                onClick={() => navigate('/sales')}
                className="w-full flex items-center gap-3 p-4 bg-gradient-to-r from-green-50 to-green-100 hover:from-green-100 hover:to-green-200 rounded-xl transition-all group"
              >
                <div className="p-2 bg-green-600 text-white rounded-lg group-hover:scale-110 transition-transform">
                  <BarChart3 className="w-5 h-5" />
                </div>
                <div className="text-left">
                  <div className="font-semibold text-gray-900">View Sales</div>
                  <div className="text-xs text-gray-600">Transaction history</div>
                </div>
              </button>

              <button
                onClick={() => navigate('/accounting/record-expense')}
                className="w-full flex items-center gap-3 p-4 bg-gradient-to-r from-red-50 to-red-100 hover:from-red-100 hover:to-red-200 rounded-xl transition-all group"
              >
                <div className="p-2 bg-red-600 text-white rounded-lg group-hover:scale-110 transition-transform">
                  <Receipt className="w-5 h-5" />
                </div>
                <div className="text-left">
                  <div className="font-semibold text-gray-900">Record Expense</div>
                  <div className="text-xs text-gray-600">Track business expenses</div>
                </div>
              </button>
            </div>

            <div className="mt-6 p-4 bg-gradient-to-r from-slate-700 to-slate-800 rounded-xl text-white">
              <div className="flex items-center gap-2 mb-2">
                <Target className="w-5 h-5" />
                <div className="font-semibold">Inventory Value</div>
              </div>
              <div className="text-2xl font-bold mt-2">
                ${(Number(metrics.inventoryValue) || 0).toLocaleString()}
              </div>
              <div className="text-sm opacity-90 mt-1">
                Current stock at cost
              </div>
            </div>
          </div>
        </div>

        {/* Expense Breakdown & Recent Transactions */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

          {/* Expense Breakdown */}
          <div className="bg-white rounded-2xl shadow-sm p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Expense Breakdown</h2>

            <div className="space-y-3">
              {expenseBreakdown.map((item, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                    <span className="font-medium text-gray-700">{item.category}</span>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-gray-900">${(Number(item.amount) || 0).toLocaleString()}</div>
                    <div className="text-xs text-gray-500">{item.percentage}%</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Recent Transactions */}
          <div className="bg-white rounded-2xl shadow-sm p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Recent Transactions</h2>

            <div className="space-y-3">
              {recentTransactions.map((transaction, index) => (
                <div
                  key={transaction.id != null ? transaction.id : index}
                  onClick={() => transaction.id != null && navigate(`/sales/${transaction.id}`)}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors cursor-pointer"
                >
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-gray-900">
                      {transaction.description}
                      {transaction.watch_reference && (
                        <span className="text-gray-500 font-normal"> · {transaction.watch_reference}</span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                      {transaction.watch_serial_number && (
                        <span className="font-mono">SN: {transaction.watch_serial_number}</span>
                      )}
                      <span>{transaction.type}</span>
                      <span>{new Date(transaction.date).toLocaleDateString()}</span>
                      {transaction.invoice_number && (
                        <span className="text-gray-400">{transaction.invoice_number}</span>
                      )}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0 ml-3">
                    <div className={`font-bold ${transaction.amount > 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {transaction.amount > 0 ? '+' : ''}${Math.abs(Number(transaction.amount) || 0).toLocaleString()}
                    </div>
                    {transaction.profit > 0 && (
                      <div className="text-xs text-green-600">
                        +${(Number(transaction.profit) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} profit
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <button
              onClick={() => navigate('/sales')}
              className="w-full mt-4 py-3 border-2 border-gray-200 rounded-xl font-medium text-gray-700 hover:bg-gray-50 transition-all"
            >
              View All Transactions
            </button>
          </div>
        </div>

        {/* Asset & Expense Management */}
        <div className="mt-8 bg-white rounded-2xl shadow-lg p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <Receipt className="w-6 h-6 text-purple-600" />
            Asset & Expense Management
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Manage Expenses */}
            <button
              onClick={() => navigate('/accounting/expenses')}
              className="p-6 border-2 border-gray-200 rounded-xl hover:border-red-500 hover:bg-red-50 transition-all text-left group"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="p-3 bg-red-100 rounded-lg group-hover:bg-red-200 transition-colors">
                  <Receipt className="w-6 h-6 text-red-600" />
                </div>
                <h3 className="font-bold text-gray-900">Expenses</h3>
              </div>
              <p className="text-sm text-gray-600 mb-3">
                View, edit, and categorize all business expenses
              </p>
              <div className="text-xl font-bold text-red-600">
                ${(Number(metrics.accountsPayable) || 0).toLocaleString()}
              </div>
              <div className="text-xs text-gray-500 mt-1">Unpaid expenses</div>
            </button>

            {/* Manage Vendors */}
            <button
              onClick={() => navigate('/accounting/vendors')}
              className="p-6 border-2 border-gray-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-all text-left group"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="p-3 bg-blue-100 rounded-lg group-hover:bg-blue-200 transition-colors">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="font-bold text-gray-900">Vendors</h3>
              </div>
              <p className="text-sm text-gray-600 mb-3">
                Manage vendor contacts and track 1099 requirements
              </p>
              <div className="text-sm text-blue-600 font-semibold">
                View all vendors →
              </div>
            </button>

            {/* Manage Assets */}
            <button
              onClick={() => navigate('/accounting/assets')}
              className="p-6 border-2 border-gray-200 rounded-xl hover:border-green-500 hover:bg-green-50 transition-all text-left group"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="p-3 bg-green-100 rounded-lg group-hover:bg-green-200 transition-colors">
                  <Package className="w-6 h-6 text-green-600" />
                </div>
                <h3 className="font-bold text-gray-900">Depreciable Assets</h3>
              </div>
              <p className="text-sm text-gray-600 mb-3">
                Track fixed assets, depreciation, and asset disposal
              </p>
              <div className="text-sm text-green-600 font-semibold">
                Manage assets →
              </div>
            </button>
          </div>
        </div>

        {/* Financial Reports - QuickBooks-style */}
        <div className="mt-8 bg-white rounded-2xl shadow-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Book className="w-6 h-6 text-blue-600" />
              Financial Reports
            </h2>
            {activeReport && (
              <button
                onClick={() => {
                  setActiveReport(null);
                  setReportData(null);
                }}
                className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 transition-colors"
              >
                <X className="w-4 h-4" />
                Close Report
              </button>
            )}
          </div>

          {!activeReport ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Profit & Loss */}
              <button
                onClick={() => fetchReport('profit-loss')}
                className="p-6 border-2 border-gray-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-all text-left group"
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-blue-100 rounded-lg group-hover:bg-blue-200 transition-colors">
                    <TrendingUp className="w-5 h-5 text-blue-600" />
                  </div>
                  <h3 className="font-bold text-gray-900">Profit & Loss</h3>
                </div>
                <p className="text-sm text-gray-600">
                  Income statement showing revenue, expenses, and net profit
                </p>
              </button>

              {/* Balance Sheet */}
              <button
                onClick={() => fetchReport('balance-sheet')}
                className="p-6 border-2 border-gray-200 rounded-xl hover:border-green-500 hover:bg-green-50 transition-all text-left group"
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-green-100 rounded-lg group-hover:bg-green-200 transition-colors">
                    <BarChart3 className="w-5 h-5 text-green-600" />
                  </div>
                  <h3 className="font-bold text-gray-900">Balance Sheet</h3>
                </div>
                <p className="text-sm text-gray-600">
                  Assets, liabilities, and equity snapshot
                </p>
              </button>

              {/* Cash Flow */}
              <button
                onClick={() => fetchReport('cash-flow')}
                className="p-6 border-2 border-gray-200 rounded-xl hover:border-purple-500 hover:bg-purple-50 transition-all text-left group"
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-purple-100 rounded-lg group-hover:bg-purple-200 transition-colors">
                    <Wallet className="w-5 h-5 text-purple-600" />
                  </div>
                  <h3 className="font-bold text-gray-900">Cash Flow</h3>
                </div>
                <p className="text-sm text-gray-600">
                  Cash coming in and going out of your business
                </p>
              </button>

              {/* General Journal */}
              <button
                onClick={() => navigate('/accounting/general-journal')}
                className="p-6 border-2 border-gray-200 rounded-xl hover:border-amber-500 hover:bg-amber-50 transition-all text-left group"
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-amber-100 rounded-lg group-hover:bg-amber-200 transition-colors">
                    <Book className="w-5 h-5 text-amber-600" />
                  </div>
                  <h3 className="font-bold text-gray-900">General Journal</h3>
                </div>
                <p className="text-sm text-gray-600">
                  View all journal entries and double-entry transactions
                </p>
              </button>

              {/* Chart of Accounts */}
              <button
                onClick={() => navigate('/accounting/chart-of-accounts')}
                className="p-6 border-2 border-gray-200 rounded-xl hover:border-teal-500 hover:bg-teal-50 transition-all text-left group"
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-teal-100 rounded-lg group-hover:bg-teal-200 transition-colors">
                    <Book className="w-5 h-5 text-teal-600" />
                  </div>
                  <h3 className="font-bold text-gray-900">Chart of Accounts</h3>
                </div>
                <p className="text-sm text-gray-600">
                  View GAAP account structure with standard numbering
                </p>
              </button>

              {/* Trial Balance */}
              <button
                onClick={() => fetchReport('trial-balance')}
                className="p-6 border-2 border-gray-200 rounded-xl hover:border-indigo-500 hover:bg-indigo-50 transition-all text-left group"
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-indigo-100 rounded-lg group-hover:bg-indigo-200 transition-colors">
                    <Receipt className="w-5 h-5 text-indigo-600" />
                  </div>
                  <h3 className="font-bold text-gray-900">Trial Balance</h3>
                </div>
                <p className="text-sm text-gray-600">
                  Debits and credits summary for all accounts
                </p>
              </button>

              {/* Journal Entries */}
              <button
                onClick={() => fetchReport('journal-entries')}
                className="p-6 border-2 border-gray-200 rounded-xl hover:border-rose-500 hover:bg-rose-50 transition-all text-left group"
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-rose-100 rounded-lg group-hover:bg-rose-200 transition-colors">
                    <ClipboardList className="w-5 h-5 text-rose-600" />
                  </div>
                  <h3 className="font-bold text-gray-900">Journal Entries</h3>
                </div>
                <p className="text-sm text-gray-600">
                  Detailed list of all accounting journal entries
                </p>
              </button>
            </div>
          ) : (
            <div>
              {reportLoading ? (
                <div className="text-center py-12">
                  <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
                  <p className="text-gray-600">Loading report...</p>
                </div>
              ) : reportData ? (
                <div className="bg-gray-50 rounded-xl p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-bold text-gray-900 capitalize">
                      {activeReport.replace('-', ' ')}
                    </h3>
                    <button
                      onClick={exportReportPDF}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <Download className="w-4 h-4" />
                      Export PDF
                    </button>
                  </div>

                  {/* Report Data Display */}
                  <div className="max-h-[600px] overflow-y-auto">
                    {renderFormattedReport()}
                  </div>

                  <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-sm text-yellow-900">
                      <strong>⚠️ Disclaimer:</strong> These reports are designed to follow Generally Accepted Accounting Principles (GAAP) and are provided for informational and estimation purposes only. Always consult with a licensed CPA or tax professional before making financial or tax decisions.
                    </p>
                  </div>
                </div>
              ) : null}
            </div>
          )}
        </div>

        {/* Helpful Tips */}
        <div className="mt-8 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl shadow-xl p-6 text-white">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-white/20 rounded-xl">
              <Target className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-lg mb-2">💡 No QuickBooks? No Problem!</h3>
              <p className="text-white/90">
                Every sale you record automatically creates accounting entries. Your financial reports update in real-time.
                We track everything for taxes (1099s, sales tax, cost basis) so you can focus on selling watches.
              </p>
            </div>
          </div>
        </div>
    </div>
  );
};

export default AccountingDashboard;
